import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Users } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { BoardTag, Bullets, RoomNumber } from "@/components/mall/BillboardKit";

const TEAL = "#0f766e";
const CLASS_IMG = "/api/files/8072bc2e-5b84-4a7e-b413-2aee29321457";

const HOW = [
    "These are hands-on printing classes and workshops. They are taught inside the shops.",
    "The shop you are in shows its class first. Classes from every other shop are listed under it.",
    "Classes are shared across the whole directory. A class is never locked to one shop.",
    "Every shop can teach. You post a class from your back office and students book it here.",
];

/** A page torn out of a notebook: blue rules, a red margin line, and the spiral holes down the left. */
function NotebookPage({ children }) {
    return (
        <div className="relative overflow-hidden border border-zinc-300 bg-white shadow-[0_30px_70px_-40px_rgba(0,0,0,0.45)]" data-testid="classes-notebook">
            <div className="pointer-events-none absolute inset-0"
                style={{ backgroundImage: "repeating-linear-gradient(180deg, transparent 0 31px, #bfdbfe 31px 32px)", backgroundPosition: "0 22px" }} />
            <div className="pointer-events-none absolute inset-y-0 left-16 w-[2px] bg-red-300 sm:left-20" />
            <div className="pointer-events-none absolute inset-y-0 left-0 flex w-12 flex-col justify-around py-6 sm:w-14" aria-hidden="true">
                {Array.from({ length: 14 }).map((_, i) => (
                    <span key={i} className="mx-auto block h-4 w-4 rounded-full border border-zinc-300 bg-zinc-100 shadow-[inset_0_2px_3px_rgba(0,0,0,0.25)]" />
                ))}
            </div>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3"
                style={{ background: "radial-gradient(circle at 6px -2px, transparent 7px, #ffffff 8px) 0 0 / 14px 14px repeat-x" }} />
            <div className="relative pl-20 pr-6 sm:pl-28 sm:pr-10">{children}</div>
        </div>
    );
}

/** Room 07 — the classroom. Every shop can teach; the shop you are in goes first. */
export default function ClassesRoom() {
    const { store } = useMall();

    return (
        <section className="relative overflow-hidden border-y border-zinc-200" data-testid="mall-room-classes"
            style={{ background: "linear-gradient(180deg, #e7f5f3 0%, #f4f4f5 100%)" }}>
            <div className="pointer-events-none absolute inset-0 opacity-50"
                style={{ backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
            <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-12">
                <NotebookPage>
                    <div className="grid items-center gap-10 py-10 lg:grid-cols-12 md:py-12">
                        <div className="lg:col-span-7">
                            <BoardTag room="classes" kind="plaza" note="the classroom" />
                            <RoomNumber n="07" room="Classes" accent={TEAL} className="mt-6" />
                            <h2 className="mt-4 font-display text-4xl font-extrabold leading-[0.94] tracking-tighter text-zinc-900 sm:text-5xl" data-testid="classes-headline">
                                LEARN TO PRINT<br /><span style={{ color: TEAL }}>IN A REAL SHOP.</span>
                            </h2>
                            <p className="mt-5 max-w-xl text-base text-zinc-600 md:text-lg" data-testid="classes-body">
                                These are hands-on classes taught by the shops on 13thav. You press a shirt, pull a transfer or
                                cut vinyl with your own hands, and you take home the thing you made.
                            </p>
                            <Bullets items={HOW} accent={TEAL} className="mt-7" testid="classes-how" />
                            {store?.name && (
                                <p className="mt-5 inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em]"
                                    style={{ borderColor: "#99f6e4", background: "#f0fdfa", color: TEAL }}
                                    data-testid="classes-featured-note">
                                    {store.name}'s classes show first while you are in this shop.
                                </p>
                            )}
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link to="/classes" data-testid="room-classes-cta"
                                    className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold uppercase tracking-wide transition-transform duration-200 hover:scale-[1.03]"
                                    style={{ background: TEAL, color: "#ffffff" }}>
                                    <GraduationCap size={17} /> See every class
                                </Link>
                                <Link to="/supplies/sell?kind=class" data-testid="room-classes-teach"
                                    className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-zinc-900 transition-colors hover:border-zinc-900">
                                    <Users size={16} /> Teach a class
                                </Link>
                            </div>
                        </div>

                        <motion.div initial={{ opacity: 0, rotate: -4, scale: 0.96 }} whileInView={{ opacity: 1, rotate: -2, scale: 1 }}
                            viewport={{ once: true }} className="lg:col-span-5">
                            <div className="relative mx-auto max-w-sm bg-white p-3 pb-10 shadow-[0_24px_50px_-24px_rgba(0,0,0,0.5)]" data-testid="classes-polaroid">
                                <span className="absolute -top-3 left-1/2 h-6 w-24 -translate-x-1/2 -rotate-3 bg-yellow-200/80" />
                                <img src={mediaUrl(CLASS_IMG)} alt="A printing workshop in progress" data-testid="classes-photo"
                                    className="h-64 w-full object-cover sm:h-72" />
                                <p className="mt-4 text-center font-display text-sm font-bold text-zinc-700">Saturday workshop. You print it yourself.</p>
                            </div>
                        </motion.div>
                    </div>
                </NotebookPage>
            </div>
        </section>
    );
}
